import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { AlertTriangle, ArrowRight, X, Bell } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const POLL_INTERVAL_MS = 45 * 1000; // 45s

/**
 * Popup central 1-a-1 que aparece cuando un supervisor/admin escala un ticket
 * a URGENTE. Se muestra al ejecutivo asignado y a su supervisor. Montado una
 * sola vez en el root de la app; hace polling mientras el usuario está logueado.
 */
export default function TicketUrgenteAnnouncements() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [open, setOpen] = useState(false);
  const intervalRef = useRef(null);
  const unauthorizedRef = useRef(false);

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchActive = useCallback(async () => {
    if (!user || !token || unauthorizedRef.current) return;
    try {
      const r = await axios.get(`${API}/tickets/urgente-anuncios/activos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = r.data?.items || [];
      if (items.length > 0) {
        setQueue((prev) => {
          const seen = new Set(prev.map((a) => a.id));
          const merged = [...prev];
          for (const it of items) if (!seen.has(it.id)) merged.push(it);
          return merged;
        });
      }
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        unauthorizedRef.current = true;
        stopPolling();
      }
    }
  }, [user, token, stopPolling]);

  useEffect(() => {
    if (!user || !token) {
      setQueue([]);
      setCurrent(null);
      setOpen(false);
      unauthorizedRef.current = false;
      stopPolling();
      return undefined;
    }
    unauthorizedRef.current = false;
    fetchActive();
    intervalRef.current = setInterval(fetchActive, POLL_INTERVAL_MS);
    return () => { stopPolling(); };
  }, [user, token, fetchActive, stopPolling]);

  useEffect(() => {
    if (!current && queue.length > 0) {
      setCurrent(queue[0]);
      setOpen(true);
    }
  }, [queue, current]);

  const dismiss = async () => {
    if (!current) return;
    const id = current.id;
    try {
      await axios.post(
        `${API}/tickets/urgente-anuncios/${id}/dismiss`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {
      // close locally; server retries on next poll
    }
    setOpen(false);
    setTimeout(() => {
      setQueue((prev) => prev.filter((a) => a.id !== id));
      setCurrent(null);
    }, 220);
  };

  const verTicket = async () => {
    const ticketId = current?.ticket_id;
    await dismiss();
    if (ticketId) navigate(`/mesa-ayuda/tickets/${ticketId}`);
  };

  if (!user || !current) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent
        className="sm:max-w-lg p-0 overflow-hidden border-0 shadow-2xl"
        data-testid="ticket-urgente-announcement-dialog"
      >
        {/* Hero banner */}
        <div className="relative bg-gradient-to-br from-red-600 via-rose-600 to-orange-500 px-6 pt-7 pb-8 text-white">
          <div className="absolute -right-12 -top-12 w-44 h-44 rounded-full bg-white/15 blur-3xl" />
          <div className="absolute -left-8 -bottom-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
          <div className="relative flex items-start gap-3">
            <div className="bg-white/25 backdrop-blur-sm rounded-xl p-2.5 ring-1 ring-white/30">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <Badge className="bg-white/95 text-red-700 border-0 font-semibold tracking-wide text-[10px] uppercase mb-2">
                <Bell className="w-2.5 h-2.5 mr-1" /> Ticket urgente
              </Badge>
              <DialogHeader className="space-y-1">
                <DialogTitle className="text-white text-xl font-bold leading-tight pr-6">
                  ¡Tienes un ticket marcado como URGENTE!
                </DialogTitle>
                <DialogDescription className="text-white/90 text-[13px] leading-relaxed">
                  Este ticket requiere atención inmediata (SLA 1 hora). Por favor revísalo lo antes posible.
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 bg-white dark:bg-slate-900 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[160px]">
              <p className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-0.5">
                Ticket #{current.ticket_numero}
              </p>
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {current.ticket_titulo}
              </p>
            </div>
          </div>

          {current.categoria_name && (
            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <ArrowRight className="w-3 h-3" />
              <span>
                Departamento: <strong className="text-slate-700 dark:text-slate-200">{current.categoria_name}</strong>
                {current.sub_categoria ? ` · ${current.sub_categoria}` : ""}
              </span>
            </div>
          )}

          {current.escalado_por && (
            <p className="text-[11px] text-slate-400 dark:text-slate-500">
              Escalado por <strong>{current.escalado_por}</strong>
            </p>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={dismiss}
              className="text-slate-500 hover:text-slate-700"
              data-testid="ticket-urgente-close-btn"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Cerrar
            </Button>
            <Button
              onClick={verTicket}
              className="bg-red-600 hover:bg-red-500 text-white shadow-md shadow-red-200/50 dark:shadow-red-900/30"
              data-testid="ticket-urgente-ver-ticket-btn"
            >
              <ArrowRight className="w-4 h-4 mr-2" /> Ver Ticket
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
